import { useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'react-bootstrap';
import { useHistory, useLocation } from 'react-router-dom';

import GenericDropdown from '../../../controls/GenericDropdown';
import Icon from '../../../controls/Icon';
import CreatePageModal from './modals/CreatePageModal';
import { useUIConfig } from './Context';
import List from '../../../controls/List';
import AddLayoutItemModal from './modals/AddLayoutItemModal';
import GenericTextBox from '../../../controls/GenericTextBox';
import BUILD_TIME_CONFIG_TYPES from '../../../../config/buildTimeConfigTypes';

const PagesEditor = () => {
    const [translate] = useTranslation();
    const location = useLocation();
    const history = useHistory();

    const { pages, layouts, applets, blocks, add, remove, edit, reorder, sortComponents, navigateToLayout, applyHighlighter, canSendUpdate, applyToLivePreview, saveChanges,
        getPropOverridesForLayout, getMergedTemplate, pageHeader, pageFooter } = useUIConfig();

    const [hasHighligher, setHasHighlighter] = useState('');
    const [selectedPage, setSelectedPage] = useState('home');
    const [selectedSection, setSelectedSection] = useState('');
    const [creatingNewPage, setCreatingNewPage] = useState(false);
    const [addingLayoutItemSection, setAddingLayoutItemSection] = useState('');

    const selectedPageLayout = useMemo(() => pages[selectedPage]?.layout.name, [pages, selectedPage]);

    const pageOptions = useMemo(() => {
        return [
            ...Object.keys(pages).map((pageName) => ({ value: pageName })),
            {
                isDivider: true,
            },
            {
                value: 'Header',
            },
            {
                value: 'Footer',
            },
        ];
    }, [pages]);

    useEffect(() => {
        if (location.state && location.state.selectedPage) {
            setSelectedPage(location.state.selectedPage);
        }
    }, [location.state]);

    const createPage = useCallback(({ name, route, layout }) => {
        const sections = {};
        layouts.find(({ name }) => name === layout).sections.forEach((sectionName) => {
            sections[sectionName] = {};
        });

        add(['pages', name], {
            route,
            layout: {
                name: layout,
                sections,
            },
        }, true);
        setAddingLayoutItemSection('');
        setSelectedPage(name);
        setCreatingNewPage(false);
    }, [layouts, add]);

    const addLayoutItem = useCallback(({ layoutItem, path }) => {
        add(path, {
            name: layoutItem,
        });
        setAddingLayoutItemSection('');
    }, [add]);

    const removeLayoutItem = useCallback(({ key, path, }) => {
        remove(path, key);
    }, [remove]);

    const editLayoutItem = useCallback(({ path, key, value, }) => {
        edit(path, key, value);
    }, [edit]);

    const reorderLayoutItems = useCallback(({ path, oldIndex, newIndex, }) => {
        reorder(path, oldIndex, newIndex);
    }, [reorder]);

    const renderHeader = useMemo(() => {
        return <div className="rowHeading py-1">
            <label className="col-auto form-label h6 mt-1">{translate("OTM_PAGES")}</label>
            <div className="col-xl-3 col-lg-4 col-md-5 col-auto">
                <GenericDropdown
                    defaultValue={selectedPage}
                    options={pageOptions}
                    size="sm"
                    grow={true}
                    onSelect={(value) => {
                        setSelectedPage(value);
                        setSelectedSection('');
                    }}
                />
            </div>
            <div className="col-auto ms-auto">
                <Button
                    variant='outline-primary'
                    className='btn-sm p-0 border-0'
                    onClick={() => setCreatingNewPage(true)}
                >
                    <Icon icon='Add' />
                </Button>
            </div>
            <div className="col-auto">
                <Button
                    disabled={!canSendUpdate}
                    onClick={() => {
                        saveChanges();
                        applyToLivePreview();
                    }}
                    variant="outline-primary"
                    className='btn-sm p-0 border-0'
                >
                    <Icon icon="Send" />
                </Button>
            </div>
        </div>
    }, [pageOptions, selectedPage, applyToLivePreview, canSendUpdate, saveChanges, translate]);

    const editRoute = useMemo(() => {
        if (pages[selectedPage]) {
            return <div className="mt-2">
                <GenericTextBox
                    label={translate('OTM_ROUTE')}
                    type='text'
                    tooltip={translate("OTM_ROUTE_HELP_TEXT")}
                    css={{ input: 'form-control-sm' }}
                    defaultValue={pages[selectedPage].route}
                    onBlur={(event) => editLayoutItem({ path: ['pages', selectedPage], key: 'route', value: event.target.value, })}
                />
            </div>
        }

        return null;
    }, [pages, selectedPage, editLayoutItem, translate]);

    const layoutsDropdown = useMemo(() => {
        if (!selectedPageLayout) {
            return null;
        }

        return <div className="mt-2">
            <GenericDropdown
                heading={translate('OTM_SELECT_LAYOUT')}
                tooltip={translate('OTM_CHANGE_LAYOUT_TOOLTIP')}
                defaultValue={selectedPageLayout}
                size="sm"
                css={{ dropdown: 'w-33' }}
                grow={true}
                options={layouts.map(({ name }) => ({ value: name }))}
                onSelect={(value) => {
                    edit(['pages', selectedPage, 'layout'], 'name', value);
                    setSelectedSection('');

                    const layoutName = pages[selectedPage].layout.name;
                    const layout = layouts.find(({ name }) => name === layoutName);

                    const newSections = layouts.find(({ name }) => name === value).sections;
                    layout.sections.forEach(section => {
                        if (!newSections.includes(section)) {
                            edit(['pages', selectedPage, 'layout', 'sections'], section, null);
                        }
                    });
                }}
            />
        </div>
    }, [layouts, pages, selectedPageLayout, selectedPage, edit, translate]);

    const renderSectionsList = useMemo(() => {
        if (selectedPage === 'Header') {
            if (pageHeader) {
                return <div>
                    <Button
                        className="mt-3"
                        size="sm"
                        onClick={() => {
                            history.replace(location.pathname, { selectedPage });
                            navigateToLayout({
                                history,
                                layoutType: BUILD_TIME_CONFIG_TYPES.APPLETS,
                                layoutName: pageHeader,
                                query: ``
                            })
                        }}>
                        {translate("OTM_EDIT_HEADER")}
                    </Button>
                </div>
            }
            return <div>
                <p className="mt-3"> {translate("OTM_NO_PAGE_HEADER")}</p>
            </div>
        }

        if (selectedPage === 'Footer') {
            if (pageFooter) {
                return <div>
                    <Button
                        className="mt-3"
                        size="sm"
                        onClick={() => {
                            history.replace(location.pathname, { selectedPage });
                            navigateToLayout({
                                history,
                                layoutType: BUILD_TIME_CONFIG_TYPES.APPLETS,
                                layoutName: pageFooter,
                                query: ``
                            })
                        }}>
                        {translate("OTM_EDIT_FOOTER")}
                    </Button>
                </div>
            }

            return <div>
                <p className="mt-3"> {translate("OTM_NO_PAGE_FOOTER")}</p>
            </div>
        }

        if (!pages[selectedPage]) {
            return null;
        }

        const layoutName = pages[selectedPage].layout.name;
        const layout = layouts.find(({ name }) => name === layoutName);
        if (!layout) {
            return <p>The layout {layoutName} is not currently supported in Configure</p>;
        }

        return <div className="mt-2">
            <label className="form-label h6">{translate("OTM_SELECT_SECTION")}</label>
            <List
                data={layout.sections.map((section, index) => ({ _id: index, name: section }))}
                attr={"name"}
                css="h5"
                selectedElement={selectedSection}
                elementId={"name"}
                onClick={(_, section) => setSelectedSection(section)}
            />
        </div>
    }, [pages, selectedPage, selectedSection, layouts, history, location.pathname, navigateToLayout, pageHeader, pageFooter, translate]);

    const renderSelectedSection = useCallback((sectionName, section) => {
        const hiddenComponentIndices = [];
        const visibleComponentIndices = [];

        const sections = section ? sortComponents(section).map(([configKey, config], i) => {
            if (config?.Hidden) {
                hiddenComponentIndices.push(i);
            }
            else {
                visibleComponentIndices.push(i);
            }

            return {
                _id: configKey,
                name: config ? config.name : null,
            };
        }) : [];

        const path = ['pages', selectedPage, 'layout', 'sections', sectionName];

        const callbacks = {
            Highlight: {
                icon: 'Highlight',
                variant: 'outline-primary',
                callback: (_event, configKey, value) => {
                    const type = applets[value] ? BUILD_TIME_CONFIG_TYPES.APPLETS : blocks[value] ? BUILD_TIME_CONFIG_TYPES.BLOCKS : BUILD_TIME_CONFIG_TYPES.COMPONENTS;

                    const initialPath = ['pages', selectedPage, 'layout', 'sections', sectionName, configKey, 'config'];
                    const currentOverrides = getPropOverridesForLayout(initialPath, type, value);

                    const { path: propEditorPath } = getMergedTemplate(initialPath, currentOverrides);
                    const { config } = getMergedTemplate(propEditorPath, {});

                    if (config.path) {
                        if (hasHighligher === value) {
                            applyToLivePreview();
                            setHasHighlighter('');
                        }
                        else {
                            applyHighlighter({ jsxName: config.path, editingPath: propEditorPath });
                            setHasHighlighter(value);
                        }
                    }
                }
            },
            Edit: {
                icon: 'Edit',
                variant: 'outline-primary',
                callback: (_event, configKey, value) => {
                    const type = applets[value] ? BUILD_TIME_CONFIG_TYPES.APPLETS : blocks[value] ? BUILD_TIME_CONFIG_TYPES.BLOCKS : BUILD_TIME_CONFIG_TYPES.COMPONENTS;
                    history.replace(location.pathname, { selectedPage });
                    navigateToLayout({
                        history,
                        layoutType: BUILD_TIME_CONFIG_TYPES.PAGES,
                        layoutName: value,
                        query: `selectedPage=${selectedPage}&sectionName=${sectionName}&configKey=${configKey}&type=${type}`
                    })
                }
            },
            Delete: {
                hiddenIndices: hiddenComponentIndices,
                icon: 'Delete',
                variant: 'outline-danger',
                callback: (_event, key) => removeLayoutItem({ path, key }),
            },
            ReAdd: {
                hiddenIndices: visibleComponentIndices,
                icon: 'Add',
                variant: 'outline-primary',
                callback: (_event, key) => edit([...path, key], 'Hidden', false),
            }
        };

        return (
            <div key={sectionName}>
                <div className="rowHeading py-1 mx-0">
                    <label className="col-auto form-label h6 mt-1">{sectionName}</label>
                    <div className="col-auto ms-auto mt-auto">
                        <Button
                            variant='outline-primary'
                            className='p-0 border-0'
                            onClick={() => setAddingLayoutItemSection(sectionName)}
                        >
                            <Icon icon='Add' />
                        </Button>
                    </div>
                </div>
                <AddLayoutItemModal
                    showApplets={true}
                    showComponents={false}
                    show={addingLayoutItemSection === sectionName}
                    onAdd={({ layoutItem }) => addLayoutItem({ layoutItem, path })}
                    onCancel={() => setAddingLayoutItemSection('')}
                />
                <List
                    data={sections}
                    attr='name'
                    drag={{ type: 'Layout', onReorder: (dragIndex, hoverIndex) => reorderLayoutItems({ path, oldIndex: dragIndex, newIndex: hoverIndex }), }}
                    selectedElement={null}
                    callbacks={callbacks}
                />
            </div>
        );
    }, [selectedPage, applets, blocks, addingLayoutItemSection, edit, sortComponents, navigateToLayout, history, location.pathname, getPropOverridesForLayout,
        getMergedTemplate, applyToLivePreview, applyHighlighter, hasHighligher, addLayoutItem, removeLayoutItem, reorderLayoutItems]);

    return (
        <div className="row">
            <CreatePageModal
                show={creatingNewPage}
                onCreate={createPage}
                onCancel={() => setCreatingNewPage(false)}
                layouts={layouts.map(({ name }) => ({ _id: name, name }))}
            />
            <div className="col-5">
                {renderHeader}
                {editRoute}
                {layoutsDropdown}
                {renderSectionsList}
            </div>
            <div className="col-1"></div>
            <div className="col-6">
                {selectedSection && renderSelectedSection(selectedSection, pages[selectedPage].layout.sections[selectedSection])}
            </div>
        </div>
    );
};

export default PagesEditor;